/* eslint-disable indent */
import { afterNextRender, Component, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { finalize } from 'rxjs';
import { getNotificationRoute, NotificationTypeEnum } from '../../../../../core/constants/notification.constant';
import { SuccessPopupComponent } from '../../../../../core/libraries/popup/components/success-popup/success-popup.component';
import {
    INotificationItem,
    INotificationListingFilterModel,
    INotificationListingModel,
} from '../../../../../core/models/notification.model';
import { NotificationService } from '../../../../../core/services/api/notification/notification.service';
import { NotificationContextService } from '../../../../../core/services/helper/notification-context/notification-context.service';
import { LoaderService } from '../../../../../core/services/loader/loader.service';

@Component({
    selector: 'app-notification-dropdown',
    standalone: false,
    templateUrl: './notification-dropdown.component.html',
    styleUrl: './notification-dropdown.component.scss',
})
export class NotificationDropdownComponent {
    protected isNotificationsLoading = signal<boolean>(true);
    protected NotificationsData = signal<INotificationListingModel | null>(null);

    constructor(
        private dialog: MatDialog,
        private loaderService: LoaderService,
        private notificationContextService: NotificationContextService,
        private notificationService: NotificationService,
        private router: Router,
    ) {
        afterNextRender(() => {
            this.loadNotifications();

            this.notificationContextService.notificationUpdated$.subscribe(() => {
                this.loadNotifications();
            });
        });
    }

    protected onNotificationClick(notification: INotificationItem): void {
        const route = getNotificationRoute(notification);
        if (route) {
            this.router.navigate(route);
        }
    }

    protected onNotificationRead(notification: INotificationItem): void {
        if (notification.isRead) {
            return;
        }

        this.loaderService.show();
        this.notificationService
            .readNotification(notification.id)
            .pipe(finalize(() => this.loaderService.hide()))
            .subscribe({
                next: () => {
                    const currentData = this.NotificationsData();
                    if (!currentData) {
                        this.notificationContextService.notifyNotificationUpdated();
                        return;
                    }
                    const updatedNotifications = currentData.notifications.map((n) =>
                        n.id === notification.id ? { ...n, isRead: true } : n,
                    );
                    this.NotificationsData.update((currentData) =>
                        currentData
                            ? {
                                  ...currentData,
                                  notifications: updatedNotifications,
                                  unreadCount: currentData.unreadCount!,
                                  actionCount: currentData.actionCount!,
                              }
                            : currentData,
                    );
                    this.notificationContextService.notifyNotificationUpdated();
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error on read notification:', error);
                },
            });
    }

    protected timeAgo(time: string): string | null {
        const relative = DateTime.fromISO(time).toRelative();
        return relative ? relative.replace(' ago', '') : null;
    }

    private loadNotifications(filter?: INotificationListingFilterModel): void {
        this.loaderService.show();
        this.notificationService
            .getNotificationListing({ ...filter, notificationType: NotificationTypeEnum.All })
            .pipe(finalize(() => this.loaderService.hide()))
            .subscribe({
                next: (response) => {
                    this.NotificationsData.set(response);
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error getting notifications:', error);
                },
            });
    }
}
